import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Activation() {
  const navigate = useNavigate();
  const [cle, setCle] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCle, setLoadingCle] = useState(true);
  const [cleValidee, setCleValidee] = useState(false);
  const [message, setMessage] = useState('');
  const [clePreChargee, setClePreChargee] = useState(false);

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const chargerMaCle = async () => {
    try {
      setLoadingCle(true);
      const token = getToken();

      if (!token) {
        console.log('Pas de token, redirection vers login...');
        navigate('/pat/connexion', { replace: true });
        return;
      }

      const response = await fetch(`${API_BASE}/pat/get_ma_cle.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('Réponse get_ma_cle:', data);

      if (data.success && data.cle_unique) {
        setCle(data.cle_unique);
        setClePreChargee(true);
        toast.success('Clé chargée automatiquement');
      } else {
        console.log('Aucune clé trouvée:', data.message);
      }
    } catch (error) {
      console.error('Erreur chargement clé:', error);
    } finally {
      setLoadingCle(false);
    }
  };

  useEffect(() => {
    chargerMaCle();
  }, []);

  const handleVerification = async () => {
    if (!cle.trim()) {
      toast.error('Veuillez saisir une clé');
      return;
    }

    setLoading(true);
    setMessage('Vérification en cours...');

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE}/pat/verifier_cle.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ cle_unique: cle }),
      });

      const data = await response.json();

      if (data.success) {
        setCleValidee(true);
        setMessage('✅ Clé valide !');
        toast.success('Clé validée avec succès');

        const expireAt = Date.now() + 8 * 60 * 60 * 1000;
        localStorage.setItem('caisse_active', 'true');
        localStorage.setItem('caisse_expire', expireAt.toString());

        // Redirection immédiate sans rechargement (après un court délai pour voir le message)
        setTimeout(() => {
          navigate('/pat/caisse', { replace: true });
        }, 800);
      } else {
        setCleValidee(false);
        setMessage(`❌ ${data.message || 'Clé invalide'}`);
        toast.error(data.message || 'Clé invalide');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('❌ Erreur de connexion');
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleAccederCaisse = () => {
    if (!cleValidee) {
      toast.error('Veuillez d’abord valider votre clé');
      return;
    }
    navigate('/pat/caisse', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-8 flex items-center justify-center">
      {/* Arrière‑plan décoratif (inchangé) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute top-1/4 -right-20 w-80 h-80 bg-emerald-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute -bottom-28 left-1/4 w-96 h-96 bg-amber-100 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Carte principale */}
        <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-3xl shadow-2xl overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-600 via-emerald-500 to-amber-500"></div>

          <div className="p-8">
            {/* En‑tête (inchangé) */}
            <div className="text-center mb-8">
              <div className="relative mx-auto mb-6 w-28 h-28">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl rotate-6 shadow-lg"></div>
                <div className="relative w-28 h-28 bg-white rounded-2xl flex items-center justify-center shadow-xl border border-gray-100">
                  <span className="text-5xl">🔑</span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 px-4 py-1.5 rounded-full text-xs font-bold mb-5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                Activation de la caisse sécurisée
              </div>

              <h1 className="text-3xl font-black bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Bienvenue
              </h1>
              <p className="text-slate-500 text-sm mt-2">
                Entrez votre clé d'activation pour accéder à la caisse
              </p>
            </div>

            {/* Formulaire */}
            <div className="space-y-6">
              {/* Champ clé */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Clé d'activation
                  {clePreChargee && (
                    <span className="ml-2 text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      Chargée automatiquement
                    </span>
                  )}
                </label>

                <div className="relative">
                  <input
                    type="text"
                    value={cle}
                    onChange={(e) => {
                      setCle(e.target.value.toUpperCase());
                      setCleValidee(false);
                      setMessage('');
                    }}
                    placeholder="ex: 1a2b3c4d5e6f..."
                    className="w-full border-2 border-slate-200 bg-slate-50/50 rounded-xl px-5 py-4 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 transition-all"
                    autoFocus
                    disabled={loadingCle}
                  />
                  {loadingCle && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {clePreChargee
                    ? '✓ Votre clé a été chargée automatiquement. Cliquez sur vérifier.'
                    : 'Si vous avez une clé, saisissez-la ci-dessus'}
                </p>
              </div>

              {/* Bouton vérification */}
              <button
                onClick={handleVerification}
                disabled={loading || !cle.trim()}
                className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 font-bold text-white shadow-lg shadow-blue-200 hover:from-blue-700 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              >
                <span className="absolute inset-0 w-0 bg-white/20 transition-all duration-300 group-hover:w-full"></span>
                <span className="relative flex items-center justify-center gap-3">
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Vérification en cours...
                    </>
                  ) : (
                    <>
                      <span className="flex w-7 h-7 items-center justify-center rounded-full bg-white/20">
                        ✓
                      </span>
                      Vérifier ma clé
                    </>
                  )}
                </span>
              </button>

              {/* Message de statut */}
              {message && (
                <div
                  className={`text-center px-5 py-3 rounded-xl border font-medium ${
                    message.includes('✅')
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : 'bg-red-50 text-red-700 border-red-100'
                  }`}
                >
                  {message}
                </div>
              )}

              {/* Bouton accès caisse */}
              <button
                onClick={handleAccederCaisse}
                className={`group w-full rounded-xl px-5 py-4 font-bold transition-all duration-300 ${
                  cleValidee
                    ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-200 hover:from-emerald-700 hover:to-emerald-800'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                <span className="flex items-center justify-center gap-3">
                  <span
                    className={`flex w-7 h-7 items-center justify-center rounded-full ${
                      cleValidee ? 'bg-white/20' : 'bg-white'
                    }`}
                  >
                    →
                  </span>
                  Accéder à la caisse
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pied de page décoratif (inchangé) */}
        <div className="mt-6 flex justify-center gap-2">
          <div className="w-12 h-1 rounded-full bg-blue-600"></div>
          <div className="w-12 h-1 rounded-full bg-emerald-500"></div>
          <div className="w-12 h-1 rounded-full bg-amber-500"></div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} - Système de gestion de caisse PAT
        </p>
      </div>
    </div>
  );
}